// crewbit-admin/src/components/schedule/WeekDayColumn.tsx
//
// Schedule Module Phase C C2 — single day-column in <WeekGrid>. Header cell
// (day-of-week + date) + body of compact <EntryTile>s + <DayEmptySlot> when no
// entries. HTML5 drag/drop receivers wired upstream by <WeekGrid>.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §3.3.

import { palette, admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import { DayEmptySlot } from './DayEmptySlot';
import { EntryTile } from './EntryTile';

export interface WeekDayColumnProps {
  date: string;
  entries: ScheduleEntry[];
  isToday: boolean;
  onEntryTap: (entryId: string) => void;
  onEntryDragStart?: (entry: ScheduleEntry, e: React.DragEvent<HTMLDivElement>) => void;
  onColumnDrop?: (date: string, droppedEntryId: string) => void;
  onEmptySlotCreate?: (date: string) => void;
  industryAccent?: string;
}

function dayHeaderLabel(iso: string): { dow: string; day: number } {
  const d = new Date(`${iso}T12:00:00`);
  return {
    dow: d.toLocaleDateString('en-US', { weekday: 'short' }),
    day: d.getDate(),
  };
}

export function WeekDayColumn({
  date,
  entries,
  isToday,
  onEntryTap,
  onEntryDragStart,
  onColumnDrop,
  onEmptySlotCreate,
  industryAccent,
}: WeekDayColumnProps) {
  const sorted = [...entries].sort((a, b) => a.start_at.localeCompare(b.start_at));
  const { dow, day } = dayHeaderLabel(date);

  return (
    <div
      data-day-column={date}
      onDragOver={onColumnDrop ? (e) => { e.preventDefault(); } : undefined}
      onDrop={onColumnDrop ? (e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/entry-id');
        if (id) onColumnDrop(date, id);
      } : undefined}
      style={{
        flex: 1, minWidth: 140,
        backgroundColor: admin.surface,
        border: `1px solid ${admin.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{
        padding: '8px 12px',
        borderBottom: `1px solid ${admin.border}`,
        display: 'flex', alignItems: 'center', gap: 6,
        backgroundColor: isToday ? palette.accentWash : undefined,
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: palette.g500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {dow}
        </span>
        <span style={{
          fontSize: 16, fontWeight: 600,
          color: isToday ? palette.accent : palette.g800,
        }}>
          {day}
        </span>
      </div>

      <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {sorted.length === 0 ? (
          <DayEmptySlot date={date} onCreate={onEmptySlotCreate} density="compact" />
        ) : (
          sorted.map((entry) => (
            <EntryTile
              key={entry.id}
              entry={entry}
              onTap={onEntryTap}
              density="compact"
              industryAccent={industryAccent}
              draggable={onEntryDragStart != null}
              onDragStart={onEntryDragStart ? (e) => onEntryDragStart(entry, e) : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}
